import React, { FC, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { Loading, Message } from '../../../shared/components';
import Page from './Page';
import { PageType } from '../../../../config/api/models/Trajectory';
import { useUser } from '../../../shared';
import { LogActionTypes, useDataLogging } from '../../../shared/data-logging';
import { UrlParams } from '../../../shared/types';
import {
  useAddPageMutation,
  useGetPagesQuery,
  useTogglePageIsPrintableMutation,
} from 'src/modules/shared/slices/page/api/pages';
import LabeledCheckbox from 'src/modules/shared/components/LabeledCheckbox/LabeledCheckbox';
import { Page as ModelPage } from 'src/config/api/models/Page';

type Props = {
  type: PageType;
  readOnly?: boolean | undefined;
};

const TrajectoryOpbouwTheoryBuilder: FC<Props> = ({ type, readOnly }) => {
  const { isTeacher, isAuthor } = useUser();
  const log = useDataLogging();
  const { bookId, trajectoryId } = useParams<UrlParams>();
  let [currentPage, setCurrentPage] = useState<ModelPage>();
  if (!trajectoryId) throw Error('No trajectoryId found!');
  if (!bookId) throw Error('No bookId found!');

  const {
    data: pageData,
    isFetching: isPageFetching,
    isLoading: isPageLoading,
    isError: isPageError,
    isSuccess: isPageSuccess,
    refetch: refetchPage,
  } = useGetPagesQuery({
    trajectoryId,
    type,
  });

  const [addPage, addPageResponse] = useAddPageMutation();
  const [toggleIsPrintable, toggleIsPrintableResponse] = useTogglePageIsPrintableMutation();

  const handlePrintableChange = useCallback(() => {
    if (isPageSuccess && pageData[0]?.id !== undefined) {
      const pageId: string = pageData[0].id;
      toggleIsPrintable({
        bookId,
        pageId,
      });
    }
  }, [toggleIsPrintable, bookId, isPageSuccess, pageData]);

  useEffect(() => {
    if (!pageData) return;
    if (pageData.length > 0) return;

    addPage({ bookId, trajectoryId, type });
  }, [addPage, pageData, type, bookId, trajectoryId]);

  useEffect(() => {
    if (!toggleIsPrintableResponse.isSuccess) return;
    setCurrentPage(toggleIsPrintableResponse.data);
    refetchPage();
  }, [toggleIsPrintableResponse, refetchPage]);

  useEffect(() => {
    if (!isPageSuccess) return;
    setCurrentPage(pageData[0]);
  }, [isPageSuccess, pageData]);

  useEffect(() => {
    if (!addPageResponse.isSuccess) return;

    if (isTeacher) {
      log(LogActionTypes.TEACHER_PAGE_CREATE, {
        bookId,
        trajectoryId,
        pageId: addPageResponse?.data?.id,
        type: addPageResponse?.data?.type,
      });
    }

    addPageResponse.reset();
    refetchPage();
  }, [isTeacher, log, bookId, trajectoryId, refetchPage, addPageResponse]);

  if (pageData && pageData.length > 1) {
    log(LogActionTypes.TRAJECTORY_ERROR_MULTIPLE_PAGES, {
      bookId,
      trajectoryId,
      error: 'Trajectory has multiple pages.',
    });
  }

  if (isPageError) {
    log(LogActionTypes.TRAJECTORY_ERROR_LOADING, {
      bookId,
      trajectoryId,
      error: 'Error loading page.',
    });
    return <Message type="error">Fout bij laden van pagina.</Message>;
  }

  if (isPageFetching || isPageLoading) {
    return <Loading />;
  }

  return (
    <>
      {isPageSuccess && pageData.length > 0 && (
        <>
          {!readOnly && isAuthor && currentPage?.isPrintable !== undefined && (
            <LabeledCheckbox
              checked={currentPage?.isPrintable}
              hideOnPrint={handlePrintableChange}
              label="Traject opnemen in print"
              tooltipMessage="Vink uit als deze pagina niet mee mag in print"
              disabled={toggleIsPrintableResponse.isLoading}
            />
          )}
          <Page type={type} id={pageData[0].id} bookId={bookId} readOnly={readOnly} />
        </>
      )}
    </>
  );
};

export default TrajectoryOpbouwTheoryBuilder;
